import { Link } from "react-router-dom";
import { Chrome } from "lucide-react";
import { EmailLink } from "./EmailLink";
import { storeUrl } from "../lib/links";
import { home } from "../content/home";

const BASE = __BASE_PATH__.replace(/\/+$/, "");

// Fathom's footer structure (founder, 7 Oct 2026): logo and one call to action on top, link columns, then the
// legal row. Only pages Intakall has. Solutions and Competitors columns come when those pages exist.
// Sign in lives here because the nav leads with Talk to us + Add to Chrome.
const PRODUCT_LINKS = [
  { label: "Overview", href: `${BASE}/` },
  { label: "How it works", href: `${BASE}/#how-it-works` },
  { label: "Features", href: `${BASE}/#features` },
  { label: "Pricing", href: `${BASE}/#pricing` },
  { label: "Sign in", href: "https://app.intakall.io" },
] as const;

const RESOURCE_LINKS = [
  { label: "Blog", href: `${BASE}/#journal` },
  // A static page built from content/guide.md, not a client-side route.
  { label: "Getting started guide", href: `${BASE}/guide` },
  { label: "FAQ", href: `${BASE}/#faq` },
] as const;

const COMPANY_ROUTES = [
  { label: "Security", to: "/security" },
  { label: "Who processes your data", to: "/subprocessors" },
  { label: "Support", to: "/support" },
  { label: "Intakall for Zoom", to: "/zoom" },
] as const;

const LEGAL_ROUTES = [
  { label: "Terms of Service", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Security", to: "/security" },
] as const;

const focus = "rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper";
const link = `font-sans text-[15px] text-paper/85 transition-colors hover:text-paper ${focus}`;
const heading = "font-sans text-[15px] text-paper/50";

export function Footer() {
  return (
    <footer className="bg-char text-paper">
      <div className="mx-auto max-w-6xl px-4 pb-10 pt-14 sm:px-6">
        <Link to="/" className={`inline-flex items-center gap-2.5 font-sans text-[26px] font-semibold tracking-[-0.02em] text-paper ${focus}`}>
          <img src={`${BASE}/mark.svg`} alt="" width={30} height={30} className="h-[30px] w-[30px]" />
          Intakall
        </Link>

        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto] lg:gap-8">
          <section aria-label="Product" className="flex flex-col gap-5">
            <h2 className={heading}>Product</h2>
            <ul className="flex flex-col gap-4">
              {PRODUCT_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <a href={href} className={link}>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <section aria-label="Resources" className="flex flex-col gap-5">
            <h2 className={heading}>Resources</h2>
            <ul className="flex flex-col gap-4">
              {RESOURCE_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <a href={href} className={link}>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <section aria-label="Company" className="flex flex-col gap-5">
            <h2 className={heading}>Company</h2>
            <ul className="flex flex-col gap-4">
              <li>
                <EmailLink email="hello@intakall.io" className={link}>
                  Talk to us
                </EmailLink>
              </li>
              {COMPANY_ROUTES.map(({ label, to }) => (
                <li key={to}>
                  <Link to={to} className={link}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <div className="lg:justify-self-end">
            <a
              href={storeUrl("footer")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 items-center gap-2 rounded-full bg-brand-400 px-6 font-sans text-[15px] font-semibold text-char transition-colors hover:bg-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper focus-visible:ring-offset-2 focus-visible:ring-offset-char"
            >
              <Chrome className="h-4 w-4" aria-hidden="true" />
              Add to Chrome, it's free
            </a>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-paper/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <nav aria-label="Legal">
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {LEGAL_ROUTES.map(({ label, to }) => (
                <li key={label}>
                  <Link to={to} className={link}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <p className="font-sans text-sm text-paper/60">{home.footer.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
